import { Trans } from '@i18n';
import { org_sector_type_enum } from '@predium/enums';

export enum BuildingFiltersKeys {
  portfolioIds = 'portfolioIds',
  economicUnitIds = 'economicUnitIds',
  buildingIds = 'buildingIds',
  classOfUses = 'classOfUses',
  typeOfUses = 'typeOfUses',
  efficiencyClasses = 'efficiencyClasses',
  energyCertificateTypes = 'energyCertificateTypes',
  noEnergyCertificate = 'noEnergyCertificate',
  energySourceTypes = 'energySourceTypes',
  taxBrackets = 'taxBrackets',
  euTaxonomyCompliance = 'euTaxonomyCompliance',
  monumentProtections = 'monumentProtections',
  heritageDistrict = 'heritageDistrict',
  milieuProtection = 'milieuProtection',
  leasehold = 'leasehold',
  buildingStates = 'buildingStates',
  minPrimaryEnergy = 'minPrimaryEnergy',
  minCo2Emissions = 'minCo2Emissions',
  minFinalEnergy = 'minFinalEnergy',
  maxPrimaryEnergy = 'maxPrimaryEnergy',
  maxCo2Emissions = 'maxCo2Emissions',
  maxFinalEnergy = 'maxFinalEnergy',
  minStrandingYear = 'minStrandingYear',
  maxStrandingYear = 'maxStrandingYear',
  districtHeatingAvailability = 'districtHeatingAvailability',
  activeStateFilter = 'activeStateFilter',
  isLod2 = 'isLod2',
  hasConsumptionData = 'hasConsumptionData',
  responsibleUserIds = 'responsibleUserIds',
  economicUnitResponsibleUserIds = 'economicUnitResponsibleUserIds',
  slfCompliant = 'slfCompliant',
}

// NOTE: Order matters, it is used to determine the order of the filters in the filter menu
export const BuildingFilters = [
  {
    key: BuildingFiltersKeys.hasConsumptionData,
    title: <Trans i18nKey="DataCollection_BuildingListFilter-hasConsumptionData" />,
    showAs: 'radio',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.activeStateFilter,
    title: <Trans i18nKey="DataCollection_BuildingListFilter-buildingSystemStatus" />,
    showAs: 'buildingSystemStatus',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.isLod2,
    title: <Trans i18nKey="General_LOD_DetailLevel-Title" />,
    showAs: 'checkbox',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.responsibleUserIds,
    title: <Trans i18nKey="General_ResponsibleUser" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.economicUnitResponsibleUserIds,
    title: (
      <Trans
        i18nKey="DataCollection_Filter_EconomicUnit_ResponsibleUser"
        values={{ orgSector: org_sector_type_enum.STANDARD }}
      />
    ),
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.buildingStates,
    title: <Trans i18nKey="General_State" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.classOfUses,
    title: <Trans i18nKey="General_ClassOfUse" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.typeOfUses,
    title: <Trans i18nKey="General_TypeOfUse" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.efficiencyClasses,
    title: <Trans i18nKey="General_EfficiencyClass" />,
    showAs: 'chip',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.energyCertificateTypes,
    title: <Trans i18nKey="General_TypeOfCertificate" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.minStrandingYear,
    title: <Trans i18nKey="General_Min" />,
    showAs: 'input',
    group: 'strandingYear',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.maxStrandingYear,
    title: <Trans i18nKey="General_Max" />,
    showAs: 'input',
    group: 'strandingYear',
    hide: true,
  },
  {
    key: BuildingFiltersKeys.euTaxonomyCompliance,
    title: <Trans i18nKey="General_EUTaxonomy" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.energySourceTypes,
    title: <Trans i18nKey="General_EnergySource" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.districtHeatingAvailability,
    title: <Trans i18nKey="General_DistrictHeatingAvailability" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.minFinalEnergy,
    title: <Trans i18nKey="General_Min" />,
    showAs: 'input',
    group: 'finalEnergy',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.maxFinalEnergy,
    title: <Trans i18nKey="General_Max" />,
    showAs: 'input',
    group: 'finalEnergy',
    hide: true, // we should as a group with min field, so we prevent double rendering
  },
  {
    key: BuildingFiltersKeys.minPrimaryEnergy,
    title: <Trans i18nKey="General_Min" />,
    showAs: 'input',
    group: 'primaryEnergy',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.maxPrimaryEnergy,
    title: <Trans i18nKey="General_Max" />,
    showAs: 'input',
    group: 'primaryEnergy',
    hide: true, // we should as a group with min field, so we prevent double rendering
  },
  {
    key: BuildingFiltersKeys.minCo2Emissions,
    title: <Trans i18nKey="General_Min" />,
    showAs: 'input',
    group: 'co2Emissions',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.maxCo2Emissions,
    title: <Trans i18nKey="General_Max" />,
    showAs: 'input',
    group: 'co2Emissions',
    hide: true, // we should as a group with min field, so we prevent double rendering
  },
  {
    key: BuildingFiltersKeys.taxBrackets,
    title: <Trans i18nKey="General_TaxBracket" />,
    showAs: 'chip',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.monumentProtections,
    title: <Trans i18nKey="General_MonumentProtection" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.heritageDistrict,
    title: <Trans i18nKey="General_HeritageDistrict" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.milieuProtection,
    title: <Trans i18nKey="General_MilieuProtection" />,
    showAs: 'checkbox',
    group: undefined,
    hide: false,
  },
  {
    key: BuildingFiltersKeys.leasehold,
    title: <Trans i18nKey="General_Leasehold" />,
    showAs: 'checkbox',
    hide: false,
  },
  {
    key: BuildingFiltersKeys.slfCompliant,
    title: <Trans i18nKey="General_SLFCompliant" />,
    showAs: 'radio',
    group: undefined,
    hide: false,
  },
] as const;
